import { useResizeMessage } from "@justhome/common/hooks";
import clsx from "clsx";
import { FC, PropsWithChildren, useRef } from "react";
import SwitchTransition from "react-transition-group/SwitchTransition";
import Transition from "react-transition-group/Transition";
import ContactStep from "./components/ContactStep";
import EquityStep from "./components/EquityStep";
import MonthlyNetIncomeStep from "./components/MonthlyNetIncomeStep";
import OffersStep from "./components/OffersStep";
import PostalCodeStep from "./components/PostalCodeStep";
import PurchasePriceStep from "./components/PurchasePriceStep";
import StepLayout from "./components/StepLayout";
import TypeOfPropertyStep from "./components/TypeOfPropertyStep";
import useAppContext from "./lib/useAppContext";
import useCobranding from "./lib/usePartner";

const PageTransition: FC<PropsWithChildren<{ state: any }>> = ({
  state,
  children,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <SwitchTransition>
      <Transition
        key={state}
        nodeRef={nodeRef}
        mountOnEnter
        unmountOnExit
        timeout={150}
      >
        {(state) => (
          <div
            ref={nodeRef}
            className={clsx("transition-all ease-in duration-75", {
              "opacity-100 scale-100": state === "entered",
              "opacity-0 scale-95": state === "entering",
              "opacity-0 pointer-events-none":
                state === "exiting" || state === "exited",
            })}
          >
            {children}
          </div>
        )}
      </Transition>
    </SwitchTransition>
  );
};

const App: FC<{ skipPostalCode: boolean }> = ({ skipPostalCode }) => {
  useResizeMessage();
  const { isLoading } = useCobranding();
  const { state } = useAppContext();
  const currentStep = state.steps[state.steps.length - 1];
  const numberOfSteps = skipPostalCode ? 5 : 6;

  if (isLoading) {
    return null;
  }
  return (
    <section className="w-full py-3xs flex flex-col flex-none">
      <StepLayout stepNumber={state.steps.length} numberOfSteps={numberOfSteps}>
        <PageTransition state={state.steps.length}>
          {currentStep === "contact" && <ContactStep />}
          {currentStep === "typeOfProperty" && (
            <TypeOfPropertyStep skipPostalCode={skipPostalCode} />
          )}
          {currentStep === "postalCode" && <PostalCodeStep />}
          {currentStep === "purchasePrice" && <PurchasePriceStep />}
          {currentStep === "monthlyNetIncome" && <MonthlyNetIncomeStep />}
          {currentStep === "equity" && <EquityStep />}
          {currentStep === "offers" && <OffersStep />}
        </PageTransition>
      </StepLayout>
    </section>
  );
};

export default App;
